import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';

import { API } from 'app/shared/constants/app-service-constants';
import { StorageService } from './offline-storage.service';
import { OfflineDownloadStatus } from '../models';

@Injectable({
  providedIn: 'root',
})
export class OfflineStatusesService {
  constructor(private http: HttpClient, private storageService: StorageService) {}

  setAssetDownloadEventStatus(
    asset,
    isOffline,
    eventStatus: OfflineDownloadStatus,
  ): Observable<any> {
    const params = this.getAssetDownloadEventParams(asset, eventStatus);
    if (isOffline) {
      return from(this.storageService.addEventsParams(params));
    }
    return this.http.post(API.UPDATE_OFFLINE_EVENT, params);
  }

  setAssetPlayEventStatus(asset, isOffline): Observable<any> {
    const params = this.getAssetPlayEventParams(asset, 'OFFLINE_STREAM_PLAY');
    params.eventType = 'OFFLINE_PLAY';
    if (isOffline) {
      return from(this.storageService.addEventsPlayParams({ ...params }));
    }
    params.eventStatus = 'ONLINE_STREAM_PLAY';
    return this.http.post(API.UPDATE_OFFLINE_EVENT, { ...params });
  }

  setDeleteEventStatus(deliveryKeyCode: string, isOffline): Observable<any> {
    const params = {
      deliveryKeyCode,
      eventStatus: 'DELETED',
      eventType: 'OFFLINE_DOWNLOAD',
    };

    if (isOffline) {
      return from(this.storageService.addEventsParams({ ...params }));
    }
    return this.http.post(API.UPDATE_OFFLINE_EVENT, { ...params });
  }

  async sendOfflineStoredEvents() {
    const params = await this.storageService.getEventsParams();
    const requests = [];

    params.forEach((paramsItem: any) => {
      requests.push(this.http.post(API.UPDATE_OFFLINE_EVENT, { ...paramsItem }));
    });

    return forkJoin(requests)
      .pipe(
        tap(() => {
          this.storageService.removeEventsParams();
        }),
      )
      .toPromise();
  }

  getAssetKeyCode(url: string): string {
    if (url.includes('asset') && url.includes('/')) {
      return url.split('asset')[1].split('/')[1];
    }

    return '';
  }

  getAssetFormat(url: string): string {
    if (url.includes('/stream/') && url.includes('/')) {
      return url.split('/stream/')[1].split('?')[0];
    }
    return '';
  }

  private getAssetDownloadEventParams(asset, eventStatus) {
    const { deliveryKeyCode, trackTitle } = asset;
    const trackAssetType =
      asset.types && asset.types.find((trackType: any) => trackType.type === 'Streaming');
    const url = trackAssetType ? trackAssetType.url : '';
    const assetKeyCode = this.getAssetKeyCode(url);
    const format = this.getAssetFormat(url);

    return {
      assetKeyCode,
      format,
      deliveryKeyCode,
      eventStatus,
      assetName: trackTitle,
      assetSize: 0,
      eventType: 'OFFLINE_DOWNLOAD',
    };
  }

  private getAssetPlayEventParams(asset, eventStatus) {
    const { deliveryKeyCode, trackTitle, assetKeyCode, format } = asset;

    return {
      assetKeyCode,
      format,
      deliveryKeyCode,
      eventStatus,
      assetName: trackTitle,
      assetSize: 0,
      eventType: 'OFFLINE_DOWNLOAD',
    };
  }
}
